import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import isEqual from 'lodash/isEqual';
import {
  generateUpdateTemplatePayload,
  useGenerateSaveTemplatePayload,
} from 'modules/campaigns/lib/hooks/useGenerateSaveTemplatePayload';
import {
  EmailContentDefinition,
  HtmlEditor,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import { addPositiveToast } from 'modules/toastContainer';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import noop, { NoOp } from 'utils/noop';
import { IS_VALID_HTML_HANDLEBARS } from 'utils/validationRegex';

import {
  DragAndDropCallback,
  GenericUpsertTemplate,
  TemplateAndContentFormDataLocale,
  TemplateEditViews,
} from '../common/types';
import { getCorrectlyCasedLocale } from '../common/utils';
import { useCampaignQueryContext } from '../context/CampaignQueryContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useErrorValidationContext } from '../context/ErrorValidationContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import {
  useUpdateEmailTemplate,
  useUpdateInAppTemplate,
  useUpdatePushTemplate,
  useUpdateSMSTemplate,
  useUpdateWebPushTemplate,
  useUpdateWhatsAppTemplate,
} from './updateTemplates';
import { Config } from './updateTemplates/types';
import { useUpdateEmbeddedTemplate } from './updateTemplates/useUpdateEmbeddedTemplate';

export const useSaveTemplate = () => {
  const { campaign } = useCampaignQueryContext();
  const { locale: preParsedLocale, view } = useQueryParamContext();

  const {
    liveTemplate,
    initialTemplate,
    setIsEditMode,
    contentEditor,
    getDragAndDropContentRef,
    setCurrentMediumFields,
  } = useEditableFieldsContext();

  const { revalidate } = useErrorValidationContext();

  const { medium, refetch, refetchTemplateAsync } = useTemplateQueryContext();
  const { allProjectLocales: projectLocales, templateDefaultLocale } = useLocalesQueryContext();

  const locale = getCorrectlyCasedLocale(preParsedLocale, projectLocales);
  const defaultLocaleTag = templateDefaultLocale?.tag || '';

  const createSaveTemplatePayload = useGenerateSaveTemplatePayload(generateUpdateTemplatePayload);

  // optional callback for template saving success
  const onSaveSuccessRef = useRef<NoOp>(refetch);
  const shouldSuppressSuccessToastRef = useRef(false);

  const toastName = campaign?.name ?? liveTemplate?.name;

  const dispatch = useDispatch();

  const useUpdateTemplateVariables: Config = {
    onSuccess: () => {
      if (!shouldSuppressSuccessToastRef.current)
        dispatch(addPositiveToast(`${toastName} was successfully updated.`));
      onSaveSuccessRef.current();

      // if we have change the messageTypeId, revalidate every locale
      if (!isEqual(liveTemplate.messageType?.id, initialTemplate.messageType?.id)) {
        revalidate();
      } else {
        revalidate(locale);
      }
      setIsEditMode(false);

      shouldSuppressSuccessToastRef.current = false;
    },
  };

  const [updateEmailTemplate] = useUpdateEmailTemplate(useUpdateTemplateVariables);

  const [updateSMSTemplate] = useUpdateSMSTemplate(useUpdateTemplateVariables);

  const [updatePushTemplate] = useUpdatePushTemplate(useUpdateTemplateVariables);

  const [updateWebPushTemplate] = useUpdateWebPushTemplate(useUpdateTemplateVariables);

  const [updateInAppTemplate] = useUpdateInAppTemplate(useUpdateTemplateVariables);

  const [updateEmbeddedTemplate] = useUpdateEmbeddedTemplate(useUpdateTemplateVariables);

  const [updateWhatsAppTemplate] = useUpdateWhatsAppTemplate(useUpdateTemplateVariables);

  const handleSaveTemplate = (
    template: TemplateAndContentFormDataLocale,
    isNewLocale: boolean,
    cb = refetch,
    supressSuccessToast = false,
  ) => {
    const completeSave = (
      templateToSave: TemplateAndContentFormDataLocale,
      upsertTemplate: GenericUpsertTemplate,
    ) => {
      const variables = createSaveTemplatePayload(
        templateToSave,
        medium,
        projectLocales,
        locale,
        contentEditor,
        undefined,
        defaultLocaleTag,
      );
      upsertTemplate({ variables });
    };

    const initSave = (upsertTemplate: GenericUpsertTemplate) => {
      onSaveSuccessRef.current = cb;

      shouldSuppressSuccessToastRef.current = supressSuccessToast;

      if (
        view === TemplateEditViews.content &&
        contentEditor === HtmlEditor.Bee &&
        getDragAndDropContentRef.current &&
        !isNewLocale
      ) {
        // call DnD's getContent (triggers DnD save) with a custom callback
        // if using drag and drop editor, get the html before saving
        // EXCEPTION: if a new locale has just been created, the editor will not be ready and its
        // internal save() operation will fail silently, so just complete the save normally in that
        // case (it is ok, because we are taking the initial values from the default locale anyway)
        const captureHtmlFn: DragAndDropCallback = ({ html }: { html: string }) => {
          const newTemplate = {
            ...template,
            mediumData: { ...template.mediumData, html },
          };

          completeSave(newTemplate, upsertTemplate);
        };
        getDragAndDropContentRef.current(captureHtmlFn);
      } else if (view === TemplateEditViews.content && contentEditor === HtmlEditor.Wysiwyg) {
        /**
         * @note For WYSIWYG editor in email and in-app templates, Smart Opt-in link handlebars can be inserted
         * via the hyperlink modal. However, the editor itself will replace the space character with `%20` if it
         * is inserted into the URL field of the create hyperlink modal. This conditional clause is a workaround
         * such that upon the user clicking the ‘Save design’ button for the template, the `%20` in the handlebar
         * will be re-replaced by a space character before saving the html content to the template data (MOB-7268)
         */
        const templateHtml =
          (template.mediumData as EmailContentDefinition | InAppContentDefinition)?.html || '';
        const linkHandlebars = templateHtml.match(IS_VALID_HTML_HANDLEBARS);

        if (linkHandlebars) {
          const newHtml = linkHandlebars.reduce((acc, handlebarString) => {
            const newHandlebarString = handlebarString.replace('%20', ' ');
            return acc.replace(handlebarString, newHandlebarString);
          }, templateHtml);

          const newTemplate = {
            ...template,
            mediumData: { ...template.mediumData, html: newHtml },
          };
          completeSave(newTemplate, upsertTemplate);
        } else {
          completeSave(template, upsertTemplate);
        }
      } else {
        completeSave(template, upsertTemplate);
      }
    };

    switch (medium) {
      case MessageMedium.Email:
        initSave(updateEmailTemplate);
        break;
      case MessageMedium.SMS:
        initSave(updateSMSTemplate);
        break;
      case MessageMedium.Push:
        initSave(updatePushTemplate);
        break;
      case MessageMedium.WebPush:
        initSave(updateWebPushTemplate);
        break;
      case MessageMedium.InApp:
        initSave(updateInAppTemplate);
        break;
      case EmbeddedMessage:
        initSave(updateEmbeddedTemplate);
        break;
      case WhatsApp:
        initSave(updateWhatsAppTemplate);
        break;
      default:
        return '';
    }
  };

  const saveTemplateLocale = useCallback(
    (templateLocale: TemplateAndContentFormDataLocale, cb = refetch) => {
      handleSaveTemplate(templateLocale, true, cb);
    },
    [liveTemplate, view, contentEditor],
  );

  const saveTemplate = useCallback(
    (cb = refetch) => {
      handleSaveTemplate(liveTemplate, false, cb);
    },
    [liveTemplate, view, contentEditor],
  );

  const saveTemplateAndRefetch = useCallback(
    (onSuccess: (data?: any) => void = noop, onError: (error?: any) => void = noop) =>
      saveTemplate(() => refetchTemplateAsync().then(onSuccess).catch(onError)),
    [saveTemplate, refetchTemplateAsync],
  );

  const silentlySaveTemplate = useCallback(
    (cb = refetch) => {
      handleSaveTemplate(liveTemplate, false, cb, true);
    },
    [liveTemplate, view, contentEditor],
  );

  const saveTemplateEditor = useCallback(
    (editorVariables: Record<string, any>, callback?: () => void) => {
      const { mediumData, ...commonFields } = liveTemplate;
      const saveLiveTemplate = {
        ...commonFields,
        mediumData: { ...mediumData, ...editorVariables },
      };
      const saveTemplateCb = () => {
        setCurrentMediumFields(editorVariables);
        refetch();
        callback?.();
      };
      handleSaveTemplate(saveLiveTemplate, false, saveTemplateCb);
    },
    [liveTemplate, view, contentEditor],
  );

  return {
    saveTemplate,
    saveTemplateLocale,
    saveTemplateEditor,
    saveTemplateAndRefetch,
    silentlySaveTemplate,
  };
};
